import styled, { css } from "styled-components";

const FONT = `'Lato', sans-serif`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999999;
  padding: 24px;
  overflow-y: auto;
`;

export const Dialog = styled.div`
  background-color: #f2f2f2;
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 920px;
  box-shadow:
    0 2.727px 6.384px rgba(0, 0, 0, 0.04),
    0 9.158px 21.443px rgba(0, 0, 0, 0.07),
    0 41px 96px rgba(0, 0, 0, 0.11);
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: ${FONT};
  max-height: calc(100vh - 48px);
  overflow-y: auto;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export const Title = styled.h3`
  margin: 0;
  font-family: ${FONT};
  font-weight: 700;
  font-size: 24px;
  color: #0e4c54;
  line-height: 1;
`;

export const CloseButton = styled.button`
  background: transparent;
  border: 0;
  padding: 0;
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #0e4c54;

  &:hover {
    opacity: 0.75;
  }
`;

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #bebebe;
  border-radius: 10px;
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

export const FieldLabel = styled.p`
  margin: 0;
  font-family: ${FONT};
  font-weight: 400;
  font-size: 16px;
  color: #5b5b5b;
  line-height: 1.1;
`;

export const RadioGroupRow = styled.div`
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
`;

export const RadioLabel = styled.label<{ $active?: boolean; $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${FONT};
  font-size: 14px;
  line-height: 1.1;
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  color: ${({ $active }) => ($active ? "#364863" : "#5b5b5b")};
  font-weight: ${({ $active }) => ($active ? 700 : 400)};
  user-select: none;
`;

export const RadioCircle = styled.span<{ $checked?: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid ${({ $checked }) => ($checked ? "#0e4c54" : "#bebebe")};
  background: #ffffff;
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${({ $checked }) =>
    $checked &&
    css`
      &::after {
        content: "";
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #0e4c54;
      }
    `}
`;

export const HiddenRadio = styled.input.attrs({ type: "radio" })`
  position: absolute !important;
  opacity: 0 !important;
  pointer-events: none;
  width: 0 !important;
  height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;

  &::before,
  &::after {
    display: none !important;
    content: none !important;
  }
`;

export const PeriodRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

export const PeriodLabel = styled.div`
  display: flex;
  flex-direction: column;
  font-family: ${FONT};
  color: #5b5b5b;

  & > span:first-child {
    font-size: 14px;
    line-height: 1.1;
  }
  & > span:last-child {
    font-size: 10px;
    line-height: 1.1;
  }
`;

export const DateField = styled.input.attrs({ type: "date" })`
  width: 160px;
  height: 40px !important;
  padding: 9px 10px !important;
  border-radius: 4px !important;
  border: 1px solid #bebebe !important;
  background: #ffffff !important;
  font-family: ${FONT};
  font-size: 12px;
  color: #364863;
  cursor: pointer;
  box-shadow: none !important;

  &::placeholder {
    color: #858585;
  }

  &:focus {
    border: 1px solid #0e4c54 !important;
    outline: none;
  }

  &:disabled {
    background: #e3e3e3 !important;
    cursor: default;
    color: #858585;
    -webkit-text-fill-color: #858585;
  }
`;

export const PeriodSeparator = styled.span`
  font-family: ${FONT};
  color: #364863;
  font-size: 12px;
`;

export const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const SectionHeaderTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 700;
  color: #0e4c54;
`;

export const SectionHeaderDescription = styled.p`
  margin: 0;
  font-family: ${FONT};
  font-size: 14px;
  color: #5b5b5b;
  line-height: 1.4;

  strong {
    font-weight: 800;
  }
`;

export const SearchBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const SearchBlockList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

export const InputsRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;
`;

export const InputGroup = styled.div<{ $grow?: boolean; $width?: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  ${({ $grow }) =>
    $grow
      ? css`
          flex: 1 0 0;
          min-width: 0;
        `
      : css`
          flex: 0 0 auto;
        `}
  ${({ $width }) => ($width ? `width: ${$width}px;` : "")}
`;

export const InputLabel = styled.span`
  font-family: ${FONT};
  font-size: 12px;
  color: #5b5b5b;
  line-height: 1;
`;

export const SearchInputWrapper = styled.div<{ $disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #bebebe;
  background: ${({ $disabled }) => ($disabled ? "#e3e3e3" : "#ffffff")};
  padding: 0 10px;

  & > svg {
    flex-shrink: 0;
    color: #858585;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 0;
  width: 100%;
  height: 100% !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 0 !important;
  border-radius: 0 !important;
  outline: none;
  background: transparent !important;
  appearance: none;
  -webkit-appearance: none;
  font-family: ${FONT};
  font-size: 12px;
  color: #364863;
  box-shadow: none !important;

  &::placeholder {
    color: #858585;
    opacity: 1;
  }

  &:focus {
    border: 0 !important;
    outline: none;
    box-shadow: none !important;
  }

  &:disabled {
    cursor: default;
    color: #858585;
    background: transparent !important;
    -webkit-text-fill-color: #858585;
    border: 0 !important;
  }
`;

export const ChipsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ChipsLabel = styled.span`
  font-family: ${FONT};
  font-size: 12px;
  color: #5b5b5b;
`;

export const ChipsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

export const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #6ee4d9;
  color: #0e4c54;
  font-family: ${FONT};
  font-size: 12px;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 999px;
  line-height: 1;
`;

export const ChipRemoveButton = styled.button`
  background: #0e4c54;
  color: #ffffff;
  border: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  line-height: 0;

  &:hover {
    opacity: 0.85;
  }

  & > svg {
    display: block;
  }
`;

export const WarningBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  background: #fffaca;
  border: 1px solid #f9b609;
  border-radius: 4px;
  padding: 5px 8px;
  color: #0f1111;
  font-family: ${FONT};
  font-size: 14px;
  line-height: 1.35;
  width: 100%;

  & > svg {
    flex-shrink: 0;
  }

  & > div {
    flex: 1 1 0;
    min-width: 0;
    text-align: left;
  }

  p {
    margin: 0;
  }

  p + p {
    margin-top: 0;
  }
`;

export const FooterRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
  margin-top: 8px;
`;

export const BaseButton = styled.button`
  height: 40px;
  padding: 0 16px;
  border-radius: 4px;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &:disabled {
    cursor: not-allowed;
  }
`;

export const OutlineButton = styled(BaseButton)`
  background: #ffffff;
  border: 1px solid #0e4c54;
  color: #0e4c54;

  &:hover:not(:disabled) {
    background: #f0f6f7;
  }
`;

export const PrimaryButton = styled(BaseButton)`
  background: #0e4c54;
  color: #ffffff;

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }

  &:disabled {
    background: #bebebe;
    color: #ffffff;
  }
`;

export const ErrorMessage = styled.p`
  margin: -8px 0 0;
  color: #e02828;
  font-family: ${FONT};
  font-size: 12px;
`;

export const TooltipAnchor = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;
  line-height: 0;

  & > svg {
    display: block;
  }
`;

export const TooltipBox = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: -6px;
  background: #ffffff;
  border-radius: 4px;
  padding: 8px;
  box-shadow:
    0 2.727px 6.384px rgba(0, 0, 0, 0.04),
    0 9.158px 21.443px rgba(0, 0, 0, 0.07),
    0 41px 96px rgba(0, 0, 0, 0.11);
  font-family: ${FONT};
  font-weight: 400;
  font-size: 12px;
  color: #364863;
  line-height: normal;
  z-index: 10;
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 150ms ease, visibility 150ms ease;

  ${TooltipAnchor}:hover &,
  ${TooltipAnchor}:focus-within & {
    opacity: 1;
    visibility: visible;
  }

  p {
    margin: 0;
    white-space: nowrap;
  }

  .tooltip-spacer {
    display: block;
    height: 12px;
  }
`;

export const DialogOverlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999999;
  padding: 24px;
`;

export const DialogBox = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 24px;
  width: 100%;
  max-width: 353px;
  box-shadow:
    0 2.727px 6.384px rgba(0, 0, 0, 0.04),
    0 9.158px 21.443px rgba(0, 0, 0, 0.07),
    0 41px 96px rgba(0, 0, 0, 0.11);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  font-family: ${FONT};
`;

export const DialogIcon = styled.div`
  width: 56px;
  height: 56px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  & > svg {
    display: block;
  }
`;

export const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  width: 100%;
`;

export const DialogTexts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
  text-align: center;
  color: #364863;
`;

export const DialogTitle = styled.p`
  margin: 0;
  font-family: ${FONT};
  font-weight: 900;
  font-size: 16px;
  line-height: 1;
  color: #364863;
`;

export const DialogText = styled.p`
  margin: 0;
  font-family: ${FONT};
  font-weight: 400;
  font-size: 14px;
  line-height: 1.35;
  color: #364863;

  strong {
    font-weight: 800;
  }
`;

export const DialogButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  width: 100%;
`;

export const DialogPrimaryButton = styled.button`
  flex: 1 0 0;
  min-width: 0;
  height: 40px;
  background: #0e4c54;
  color: #ffffff;
  border: 0;
  border-radius: 4px;
  font-family: ${FONT};
  font-weight: 700;
  font-size: 14px;
  letter-spacing: -0.21px;
  cursor: pointer;

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }

  &:disabled {
    background: #bebebe;
    cursor: not-allowed;
  }
`;

export const DialogOutlineButton = styled.button`
  flex: 1 0 0;
  min-width: 0;
  height: 40px;
  background: #ffffff;
  color: #0e4c54;
  border: 1px solid #0e4c54;
  border-radius: 4px;
  font-family: ${FONT};
  font-weight: 700;
  font-size: 14px;
  letter-spacing: -0.21px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #f0f6f7;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const DialogSingleButton = styled(DialogPrimaryButton)`
  flex: 0 0 139px;
`;

